import React from 'react';
import { DataGridColumn } from '../../design-system';
import { CalculationStrategies } from '../../@types/samples/types';
import { ANALYSIS_LABELS, formatNumber } from './helpers';
import { SummaryRow } from './types';
import styles from './SamplesSummaryPage.module.css';

const ANALYSIS_CLASSES: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: styles.tagLiao,
  [CalculationStrategies.LAMBDA]: styles.tagLambda,
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: styles.tagWaterOil,
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: styles.tagGasOil,
};

const numberCell = (field: keyof SummaryRow, digits = 4) => (row: SummaryRow): React.ReactNode => (
  <span className={row.isAverage ? styles.avgValue : undefined}>
    {formatNumber(row[field] as number, digits)}
  </span>
);

export interface BuildColumnsArgs {
  depthUnit: 'm' | 'ft';
}

export const buildColumns = ({ depthUnit }: BuildColumnsArgs): DataGridColumn<SummaryRow>[] => [
  {
    key: 'sample_code',
    header: 'Sample',
    sortable: true,
    editable: true,
    width: '130px',
    getValue: row =>
      row.isAverage ? <span className={styles.avgLabel}>Average</span> : row.sample_code,
  },
  {
    key: 'wellName',
    header: 'Well',
    sortable: true,
    editable: false,
    width: '130px',
    getValue: row => (row.isAverage ? '' : row.wellName),
  },
  {
    key: 'analyses',
    header: 'Analysis',
    width: '150px',
    editable: false,
    getValue: row => {
      if (row.isAverage) return '';
      if (!row.analyses.length) return <span className={styles.statusPending}>Pending</span>;
      return (
        <div className={styles.analysisTags}>
          {row.analyses.map(strategy => (
            <span key={strategy} className={`${styles.analysisTag} ${ANALYSIS_CLASSES[strategy]}`}>
              {ANALYSIS_LABELS[strategy]}
            </span>
          ))}
        </div>
      );
    },
  },
  { key: 'depth', header: `Depth (${depthUnit})`, sortable: true, editable: true, align: 'right', width: '110px', getValue: numberCell('depth', 2) },
  { key: 'porosity', header: 'Porosity', sortable: true, editable: true, align: 'right', width: '110px', getValue: numberCell('porosity') },
  { key: 'permeability', header: 'Permeability (mD)', sortable: true, editable: true, align: 'right', width: '150px', getValue: numberCell('permeability', 2) },
  { key: 'fzi', header: 'FZI', sortable: true, editable: false, align: 'right', width: '90px', getValue: numberCell('fzi') },
  { key: 'ko', header: 'Ko @ Swir (mD)', sortable: true, editable: true, align: 'right', width: '140px', getValue: numberCell('ko', 2) },
  { key: 'krw', header: 'Krw @ Sor', sortable: true, editable: true, align: 'right', width: '120px', getValue: numberCell('krw') },
  { key: 'swi', header: 'Swi', sortable: true, editable: true, align: 'right', width: '90px', getValue: numberCell('swi') },
  { key: 'qv', header: 'Qv', sortable: true, editable: true, align: 'right', width: '90px', getValue: numberCell('qv') },
  { key: 'salinity', header: 'Salinity', sortable: true, editable: true, align: 'right', width: '100px', getValue: numberCell('salinity', 2) },
  { key: 'kro_absolute', header: 'Kro Absolute', sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('kro_absolute') },
  { key: 'krw_absolute', header: 'Krw Absolute', sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('krw_absolute') },
  { key: 'krg_absolute', header: 'Krg Absolute', sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('krg_absolute') },
  { key: 'sor_w', header: 'SorW', sortable: true, editable: false, align: 'right', width: '90px', getValue: numberCell('sor_w') },
  { key: 'no_w', header: 'No', sortable: true, editable: false, align: 'right', width: '80px', getValue: numberCell('no_w') },
  { key: 'nw', header: 'Nw', sortable: true, editable: false, align: 'right', width: '80px', getValue: numberCell('nw') },
  { key: 'true_sor_w', header: 'SorW Refined', sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('true_sor_w') },
  { key: 'true_no_w', header: 'No Refined', sortable: true, editable: false, align: 'right', width: '110px', getValue: numberCell('true_no_w') },
  { key: 'true_nw', header: 'Nw Refined', sortable: true, editable: false, align: 'right', width: '110px', getValue: numberCell('true_nw') },
  { key: 'true_krw', header: "Krw' Refined", sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('true_krw') },
  { key: 'true_kro_w', header: "Krow' Refined", sortable: true, editable: false, align: 'right', width: '130px', getValue: numberCell('true_kro_w') },
  { key: 'sor_g', header: 'SorG', sortable: true, editable: false, align: 'right', width: '90px', getValue: numberCell('sor_g') },
  { key: 'no_g', header: 'No Gas', sortable: true, editable: false, align: 'right', width: '90px', getValue: numberCell('no_g') },
  { key: 'ng', header: 'Ng', sortable: true, editable: false, align: 'right', width: '80px', getValue: numberCell('ng') },
  { key: 'true_sor_g', header: 'SorG Refined', sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('true_sor_g') },
  { key: 'true_no_g', header: 'No Gas Refined', sortable: true, editable: false, align: 'right', width: '130px', getValue: numberCell('true_no_g') },
  { key: 'true_ng', header: 'Ng Refined', sortable: true, editable: false, align: 'right', width: '110px', getValue: numberCell('true_ng') },
  { key: 'true_krg', header: "Krg' Refined", sortable: true, editable: false, align: 'right', width: '120px', getValue: numberCell('true_krg') },
  { key: 'true_kro_g', header: "Krog' Refined", sortable: true, editable: false, align: 'right', width: '130px', getValue: numberCell('true_kro_g') },
];
